import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Every button in the app.
 *
 * This started as shadcn's generated button and is no longer it: that one
 * speaks shadcn's semantic palette (`bg-primary`, `border-input`) in 8px-tall
 * sizes, and this app has its own tokens, a 44px floor and exactly two radii.
 * shadcn is raw material — see CLAUDE.md. A `shadcn add` that overwrites this
 * file has to be re-done, same as the token block in tokens.css.
 *
 * Six variants, each with a real call site, and no size axis. Height comes
 * from the 44px floor in tokens.css, except that a full-width button is 52 —
 * a block button is always a form's submit or a sheet's confirm, and at 44 it
 * read thinner than the 52px field it sits under.
 *
 *   primary      the one accent action on a screen, lower half
 *   secondary    its partner in a pair — Cancel next to Save
 *   destructive  the confirm step of a delete, never the trigger
 *   quiet        a text action that isn't the primary one
 *   danger       a text action that destroys something, e.g. "Ta bort uppgift"
 *   meta         a small disclosure showing current state, e.g. the composer's list
 *   icon         a 44px square carrying one glyph and an aria-label
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 text-body font-semibold transition-colors outline-none select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary: "rounded-pill bg-accent px-5 text-on-accent",
        secondary: "rounded-pill border border-line bg-surface px-5 text-ink",
        // `surface`, not `on-accent`, on alert: it is white in light and the
        // dark card colour in dark, where alert is pale — so it contrasts with
        // alert in both modes, which a fixed white would not.
        destructive: "rounded-pill bg-alert px-5 text-surface",
        quiet: "text-accent",
        danger: "text-alert",
        meta: "gap-1 text-meta font-normal text-muted [&_svg:not([class*='size-'])]:size-3",
        icon: "grid size-11 place-items-center text-muted",
      },
      /** Full width. Inside a flex row, pair it with `*:flex-1` on the row. */
      block: {
        true: "h-13 w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      block: false,
    },
  },
);

function Button({
  className,
  variant,
  block,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, block, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
